import {permiteRol} from '../../utils/permisosRol';
import { listarEntradasPrivadas, consumirEntradaEscaner } from '../../utils/reservasPrivadas';
import { ESTADOS_QR } from '../../utils/estadosQr';
import { precioEntradaParaMostrar, resumenPreciosEntradas } from '../../utils/dinero';
import { useAuth } from '../../context/AuthContext';
import { puedeAccederEvento } from '../../utils/accessPolicy';
// src/pages/entradas/AdminEntradas.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase/firebaseConfig';
import { collection, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { getDocs, getDoc } from '../../utils/firestoreRead';
import './AdminEntradas.css';
import { exportCSV, exportPDF } from '../../utils/exportReport';
import { verificarAccesoEvento } from '../../utils/eventAccess';

export default function AdminEntradas({ soloLectura = false }) {
  const { eventoId } = useParams();
  const navigate = useNavigate();

  // Determinar si este panel debe funcionar en modo solo lectura
  const { profile } = useAuth();
  const [eventoInfo, setEventoInfo] = useState(null);
  const rolActual = profile?.rol;
  const puedeEditar=!soloLectura&&permiteRol(profile,'entradas.editar')&&puedeAccederEvento(profile,eventoInfo,'reportes');
  const esSoloLectura=soloLectura||!puedeAccederEvento(profile,eventoInfo,'reportes')||(!permiteRol(profile,'entradas.editar')&&!permiteRol(profile,'entradas.eliminar'));
  const puedeEliminar = permiteRol(profile,'entradas.eliminar');

  const [reservasEconomicas, setReservasEconomicas] = useState({});
  const [entradas, setEntradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seleccionados, setSeleccionados] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    const fetchEntradas = async () => {
      setLoading(true);

      if (!eventoId) {
        setEntradas([]);
        setLoading(false);
        return;
      }

      const acceso = await verificarAccesoEvento(eventoId, 'reportes');
      if (!acceso.permitido) {
        alert('No tienes acceso a este evento.');
        navigate('/entradas/admin');
        return;
      }

      const eventoSnap = await getDoc(doc(db, 'eventos', eventoId));
      if (!eventoSnap.exists()) {
        alert('El evento no existe.');
        navigate('/entradas/admin');
        return;
      }

      const eventoData = eventoSnap.data();
      setEventoInfo({ ...eventoData, id: eventoSnap.id });
      if (!eventoData?.empresaId) {
        setEntradas([]);
        alert('Este evento no tiene empresa asignada. Elimínalo o recréalo correctamente.');
        navigate('/entradas/admin');
        return;
      }

      // 🔹 Cargamos SOLO entradas válidas del evento y de su empresa.
      // Las entradas nuevas deben guardar empresaId desde GenerarEntradas.jsx.
      const qy = query(
        collection(db, 'entradas'),
        where('eventoId', '==', eventoId),
        where('empresaId', '==', eventoData.empresaId)
      );
      let data;
      if (rolActual === 'owner') { const snapshot = await getDocs(qy); data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })); }
      else data = await listarEntradasPrivadas(eventoId);

      // Ordenamos en el cliente para evitar depender de un índice compuesto en Firestore
      data.sort((a, b) => {
        const fechaA = a.createdAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
        const fechaB = b.createdAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
        return fechaB - fechaA;
      });

      // ==== MAPA DE LOTES (compatibilidad con cosas viejas) ====
      const loteIds = [...new Set(data.map((x) => x.loteId).filter(Boolean))];
      const loteMap = {};
      await Promise.all(
        loteIds.map(async (lid) => {
          const ref = doc(db, 'lotesEntradas', lid);
          try {
            const s = await getDoc(ref);
            if (s.exists()) loteMap[lid] = s.data();
          } catch (error) {
            // Un lote legacy sin evento verificable no se expone ni bloquea el reporte.
            if (error.code !== 'permission-denied') throw error;
          }
        })
      );

      // Los nombres históricos proceden del snapshot, nunca del perfil actual.
      // 🔄 Normalizamos datos finales
      data = data.map((x) => ({
        ...x,
        // nombre de quien generó (usuario o lote antiguo)
        creadoPorNombre:
          x.creadoPorNombre ||
          (x.loteId && loteMap[x.loteId]?.creadoPorNombre) ||
          '-',

        // Lote: usamos comboNombre si no hay loteId
        loteTexto: x.loteId || x.comboNombre || '',
        // Evento: usamos eventoNombre si no hay origen

      }));

      const reservasMap = {};
      await Promise.all([...new Set(data.map((e) => e.reservaId).filter(Boolean))].map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'reservas', id));
          if (snap.exists()) {
            const reserva = snap.data();
            if (reserva.eventoId === eventoId && reserva.empresaId === eventoData.empresaId) reservasMap[id] = { ...reserva, id };
          }
        } catch (error) { console.warn('No se pudo consultar el importe histórico de una reserva', error.code); }
      }));
      setReservasEconomicas(reservasMap);
      setEntradas(data);
      setLoading(false);
    };

    fetchEntradas();
  }, [eventoId, navigate, rolActual]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return entradas;
    return entradas.filter((e) => {
      const comprador = (e.comprador || '').toLowerCase();
      const creadorNombre = (e.creadoPorNombre || '').toLowerCase();
      const loteTexto = (e.loteTexto || '').toLowerCase();
      return (
        e.id.toLowerCase().includes(t) ||
        comprador.includes(t) ||
        creadorNombre.includes(t) ||
        loteTexto.includes(t)
      );
    });
  }, [q, entradas]);

  const marcarUsada = async (id) => {
    if (!puedeEditar) return;
    const entrada=entradas.find(e=>e.id===id);
    if(entrada?.reservaId) await consumirEntradaEscaner(id);
    else await updateDoc(doc(db, 'entradas', id), { estado: 'usada' });
    setEntradas((prev) =>
      prev.map((e) => (e.id === id ? { ...e, estado: 'usada' } : e))
    );
  };

  // 🔹 Borra entrada y, si queda todo vacío, borra colección de lotes
  const eliminarEntrada = async (id) => {
    if (esSoloLectura || !puedeEliminar) return;
    if (entradas.find(e => e.id === id)?.reservaId) return alert('Las entradas de reserva se conservan para mantener su historial.');
    if (!window.confirm('¿Eliminar esta entrada?')) return;

    await deleteDoc(doc(db, 'entradas', id));
    setEntradas((prev) => prev.filter((e) => e.id !== id));
    setSeleccionados((prev) => prev.filter((x) => x !== id));

    await limpiarLotesSiNoHayEntradas();
  };

  const toggleSeleccion = (id) => {
    setSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = (checked) => {
    setSeleccionados(checked ? filtered.map((x) => x.id) : []);
  };

  const marcarMasivo = async () => {
    if (!puedeEditar) return;
    if (!seleccionados.length) return;
    if(entradas.some(e=>seleccionados.includes(e.id)&&(e.estado!=='no_usada'||(e.reservaId&&rolActual!=='owner')))) return alert('Selecciona entradas no usadas y autorizadas.');
    if (!window.confirm('¿Marcar seleccionados como USADA?')) return;

    await Promise.all(
      seleccionados.map((id) =>
        entradas.find(e=>e.id===id)?.reservaId ? consumirEntradaEscaner(id) : updateDoc(doc(db, 'entradas', id), { estado: 'usada' })
      )
    );
    setEntradas((prev) =>
      prev.map((e) =>
        seleccionados.includes(e.id) ? { ...e, estado: 'usada' } : e
      )
    );
    setSeleccionados([]);
  };

  const eliminarMasivo = async () => {
    if (esSoloLectura || !puedeEliminar) return;
    if (!seleccionados.length) return;
    if (entradas.some(e => seleccionados.includes(e.id) && e.reservaId)) return alert('No puedes borrar entradas pertenecientes a reservas.');
    if (!window.confirm('¿Eliminar seleccionados?')) return;

    await Promise.all(
      seleccionados.map((id) => deleteDoc(doc(db, 'entradas', id)))
    );
    setEntradas((prev) => prev.filter((e) => !seleccionados.includes(e.id)));
    setSeleccionados([]);
    await limpiarLotesSiNoHayEntradas();
  };

  // 📊 Estadísticas (solo dos estados: "usada" / "no_usada")
  const totalEntradas = entradas.length;
  const resumenEconomico = resumenPreciosEntradas(entradas, reservasEconomicas);
  const usadas = entradas.filter((e) => e.estado === 'usada').length;
  const disponibles = entradas.filter((e) => e.estado === 'no_usada').length;

  // Config columnas export
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'comprador', label: 'Comprador' },
    { key: 'creadoPorNombre', label: 'Generado por' },
    { key: 'loteTexto', label: 'Lote' },
    { key: 'eventoNombre', label: 'Evento' },
    { key: 'precioTexto', label: 'Precio' },
    { key: 'estado', label: 'Estado' },
    { key: 'createdAtFmt', label: 'Creado' },
  ];

  const mapRows = (arr) =>
    arr.map((e) => ({
      ...e,
      estado:rolActual==='owner'||['anulada','bloqueada'].includes(e.estado)?e.estado:'',
      precioTexto: precioEntradaParaMostrar(e, reservasEconomicas[e.reservaId])?.toFixed(2) ?? 'Sin valorar',
      createdAtFmt:
        e.createdAt?.toDate?.().toLocaleString() ||
        '',
    }));

  const buildStats = () => ({
    total: filtered.length,
    ...(rolActual==='owner'?{usadas: filtered.filter((e) => e.estado === 'usada').length,pendientes: filtered.filter((e) => e.estado === 'no_usada').length}:{}),
    ...resumenPreciosEntradas(filtered, reservasEconomicas),
  });

  const handleExportCSV = () => {if(!permiteRol(profile,'entradas.exportar'))return;
    exportCSV(
      mapRows(filtered),
      buildStats(),
      columns,
      `reporte_entradas_${new Date().toISOString().slice(0, 10)}.csv`,
      'Entradas'
    );
  };

  const handleExportPDF = () => {if(!permiteRol(profile,'entradas.exportar'))return;
    exportPDF(
      mapRows(filtered),
      buildStats(),
      columns,
      `reporte_entradas_${new Date().toISOString().slice(0, 10)}.pdf`,
      'Entradas'
    );
  };

  if (loading) {
    return (
      <div className="admin-entradas admin-entradas--loading">
        Cargando entradas…
      </div>
    );
  }

  return (
    <div className="admin-entradas">
      <div className="ae-header">
        <div>
          <h2>Administrar Entradas</h2>
          <p className="ae-subtitle">
            Revisa, filtra y exporta las entradas del evento seleccionado.
          </p>
        </div>
        {!esSoloLectura && (
          <div className="ae-header-actions">
            <a href="/entradas" className="btn btn-primary">
              + Generar entradas
            </a>
          </div>
        )}
      </div>

      {/* Resumen */}
      <div className="ae-summary">
        <div className="ae-summary-card">
          <span className="ae-summary-label">Total de entradas</span>
          <span className="ae-summary-value">{totalEntradas}</span>
        </div>
        <div className="ae-summary-card">
          <span className="ae-summary-label">Valor de entradas (sin consumo)</span>
          <span className="ae-summary-value">{resumenEconomico.totalBs === null ? 'Sin valorar' : 'Bs ' + resumenEconomico.totalBs.toFixed(2)}</span>
          {resumenEconomico.sinPrecio > 0 && <small>{resumenEconomico.sinPrecio} entradas pendientes de valorar; total parcial</small>}
        </div>
        {rolActual==='owner'&&<div className="ae-summary-card ae-summary-card--ok">
          <span className="ae-summary-label">No usadas</span>
          <span className="ae-summary-value">{disponibles}</span>
        </div>}
        {rolActual==='owner'&&<div className="ae-summary-card ae-summary-card--warn">
          <span className="ae-summary-label">Usadas</span>
          <span className="ae-summary-value">{usadas}</span>
        </div>}
      </div>

      {/* Barra de filtros / acciones */}
      <div className="ae-toolbar">
        <input
          className="ae-search"
          placeholder="Buscar por ID, comprador, generado por o lote"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="ae-toolbar-right">
          {!esSoloLectura && seleccionados.length > 0 && (
            <div className="ae-bulk-actions">
              <button className="btn btn-secondary" onClick={marcarMasivo}>
                Marcar usadas ({seleccionados.length})
              </button>
              <button className="btn btn-danger" onClick={eliminarMasivo} disabled={!puedeEliminar}>
                Eliminar ({seleccionados.length})
              </button>
            </div>
          )}

          <div className="ae-export">
            <button className="btn btn-ghost" hidden={!permiteRol(profile,'entradas.exportar')} onClick={handleExportCSV}>
              CSV
            </button>
            <button className="btn btn-ghost" hidden={!permiteRol(profile,'entradas.exportar')} onClick={handleExportPDF}>
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="ae-table-wrapper">
        <table className="ae-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    seleccionados.length > 0 &&
                    seleccionados.length === filtered.length
                  }
                  onChange={(e) => selectAll(e.target.checked)}
                  disabled={esSoloLectura || filtered.length === 0}
                />
              </th>
              <th>ID</th>
              <th>Comprador</th>
              <th>Generado por</th>
              <th>Lote</th>
              <th>Evento</th>
              <th>Precio</th>
              <th>Estado</th>
              <th>Creado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="ae-empty">
                  No se encontraron entradas con los filtros actuales.
                </td>
              </tr>
            ) : (
              filtered.map((e) => {
                const fecha =
                  e.createdAt?.toDate?.() || e.createdAt?.toDate?.() || null;
                return (
                  <tr key={e.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={seleccionados.includes(e.id)}
                        onChange={() => toggleSeleccion(e.id)}
                        disabled={esSoloLectura}
                      />
                    </td>
                    <td className="mono">{e.id}</td>
                    <td>{e.comprador || '-'}</td>
                    <td>{e.creadoPorNombre || '-'}</td>
                    <td className="mono">{e.loteTexto || '-'}</td>
                    <td>{e.eventoNombre || '-'}</td>
                    <td>Bs {precioEntradaParaMostrar(e, reservasEconomicas[e.reservaId])?.toFixed(2) ?? 'Sin valorar'}</td>
                    <td>
                      <span
                        className={
                          'ae-badge ' +
                          (rolActual==='owner' && e.estado === 'usada'
                            ? 'ae-badge--used'
                            : 'ae-badge--pending')
                        }
                      >
                        {rolActual==='owner'?(ESTADOS_QR[e.estado] || 'Estado desconocido'):['anulada','bloqueada'].includes(e.estado)?ESTADOS_QR[e.estado]:'—'}
                      </span>
                    </td>
                    <td>{fecha ? fecha.toLocaleString() : '-'}</td>
                    <td>
                      {!esSoloLectura ? (
                        <div className="ae-actions">
                          {e.estado === 'no_usada' && (!e.reservaId || rolActual === 'owner') && (
                            <button
                              className="btn btn-link"
                              onClick={() => marcarUsada(e.id)}
                            >
                              Marcar usada
                            </button>
                          )}
                          <button
                            className="btn btn-link btn-link--danger"
                            disabled={!puedeEliminar || !!e.reservaId}
                            title={e.reservaId ? 'Las entradas de reserva conservan su historial.' : ''}
                            onClick={() => eliminarEntrada(e.id)}
                          >
                            Eliminar
                          </button>
                        </div>
                      ) : (
                        <span className="ae-solo-lectura">Solo lectura</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ==== helpers internos ==== */

// Si ya no quedan entradas, elimina todos los lotes
async function limpiarLotesSiNoHayEntradas() {
  const snap = await getDocs(collection(db, 'entradas'));
  if (snap.empty) {
    const lotesSnap = await getDocs(collection(db, 'lotesEntradas'));
    await Promise.all(
      lotesSnap.docs.map((d) => deleteDoc(doc(db, 'lotesEntradas', d.id)))
    );
    console.log('✅ No quedan entradas, se eliminó lotesEntradas.');
  }
}
